import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import api from '../services/api';
import useVaultItems from '../hooks/useVaultItems';
import { Card, HealthBar, FeatureBanner, EmptyState, PillButton } from '../ui';
import { ReactComponent as ActivityIcon } from '../assets/icons/activity.svg';
import { ReactComponent as AlertTriangleIcon } from '../assets/icons/alert-triangle.svg';
import { ReactComponent as RepeatIcon } from '../assets/icons/repeat.svg';
import { ReactComponent as CalendarIcon } from '../assets/icons/calendar.svg';
import { ReactComponent as ShieldIcon } from '../assets/icons/shield.svg';
import { ReactComponent as ShieldCheckIcon } from '../assets/icons/shield-check.svg';
import { ReactComponent as KeyIcon } from '../assets/icons/key-round.svg';
import { ReactComponent as GlobeIcon } from '../assets/icons/globe.svg';
import { ReactComponent as ExternalLinkIcon } from '../assets/icons/external-link.svg';
import './Health.css';

// Un cruce por id contra la lista ligera del vault (título, usuario) para
// no tener que descifrar nada aquí: /security/health ya no expone contraseñas.
//   field: la bandera por ítem en la respuesta (por defecto, la propia clave).
//   needs: comprobación de Watchtower (health.checks) que tiene que estar activa para que salga la fila.
//   hideWhen: la fila sobra si esa comprobación está activa (noTotp lo sustituye twoFactorAvailable).
const ROW_DEFS = [
  { key: 'weak', icon: AlertTriangleIcon, tone: 'danger' },
  { key: 'reused', icon: RepeatIcon, tone: 'warn' },
  { key: 'old', icon: CalendarIcon, tone: 'warn' },
  { key: 'noTotp', icon: ShieldIcon, tone: 'accent', hideWhen: 'twoFactorAvailable' },
  { key: 'pwned', icon: GlobeIcon, tone: 'danger', needs: 'pwnedPasswords' },
  { key: 'breachedSites', field: 'breached', icon: AlertTriangleIcon, tone: 'danger', needs: 'breachedSites' },
  { key: 'twoFactorAvailable', icon: ShieldCheckIcon, tone: 'accent', needs: 'twoFactorAvailable' },
  { key: 'passkeysAvailable', icon: KeyIcon, tone: 'accent', needs: 'passkeysAvailable' },
  // Claves SSH (informativas: no cuentan en la puntuacion). Salen solo si hay alguna, y su recuento viene de health.ssh.
  { key: 'sshWeakRsa', ssh: 'rsaWeak', icon: KeyIcon, tone: 'accent' },
  { key: 'sshNoPassphrase', ssh: 'noPassphrase', icon: KeyIcon, tone: 'accent' }
];

// Qué lista pública alimenta cada comprobación (health.feedsUpdatedAt).
const FEED_OF_CHECK = { breachedSites: 'breaches', twoFactorAvailable: 'twoFactor', passkeysAvailable: 'passkeys' };

// Atribuciones que exigen las licencias de las listas (CC BY 4.0 / MIT). Son nombres propios y la
// fórmula de 2FA Directory la pide literal, así que no se traducen.
const ATTRIBUTIONS = [
  { check: 'breachedSites', href: 'https://haveibeenpwned.com', text: 'Breach data from Have I Been Pwned (CC BY 4.0)' },
  { check: 'twoFactorAvailable', href: 'https://2fa.directory', text: 'Data sourced from 2FA Directory by 2factorauth' },
  { check: 'passkeysAvailable', href: 'https://passkeys.2fa.directory', text: 'Passkey data from Passkeys Directory by 2factorauth (CC BY 4.0)' }
];

function Health() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const rfidId = localStorage.getItem('rfidId');
  const { items } = useVaultItems({ limit: 200 });

  const [health, setHealth] = useState(null);
  const [loadingHealth, setLoadingHealth] = useState(true);
  const [checkingBreach, setCheckingBreach] = useState(false);
  const [selected, setSelected] = useState(null);

  const loadHealth = useCallback((withBreach) => {
    if (!rfidId) return;
    if (withBreach) setCheckingBreach(true); else setLoadingHealth(true);
    api.get(`/security/health/${rfidId}`, { params: withBreach ? { breach: 1 } : {} })
      .then((res) => setHealth(res.data))
      .catch(() => setHealth(null))
      .finally(() => { setLoadingHealth(false); setCheckingBreach(false); });
  }, [rfidId]);

  useEffect(() => { loadHealth(false); }, [loadHealth]);

  const itemById = useMemo(() => {
    const map = new Map();
    items.forEach((i) => map.set(String(i.id), i));
    return map;
  }, [items]);

  // Servidores anteriores a Watchtower no mandan `checks`: se comportan como antes (pwned siempre visible).
  const checks = useMemo(() => health?.checks || { pwnedPasswords: true }, [health]);

  const rows = useMemo(() => {
    if (!health) return [];
    return ROW_DEFS
      .filter((def) => (!def.needs || checks[def.needs]) && !(def.hideWhen && checks[def.hideWhen]) && (!def.ssh || health.ssh?.total > 0))
      .map((def) => ({ ...def, count: def.ssh ? (health.ssh?.[def.ssh] ?? 0) : (health.summary[def.key] ?? 0) }));
  }, [health, checks]);

  const selectedDef = useMemo(() => ROW_DEFS.find((d) => d.key === selected), [selected]);

  const selectedItems = useMemo(() => {
    if (!selectedDef || !health) return [];
    const field = selectedDef.field || selectedDef.key;
    return (selectedDef.ssh ? (health.ssh?.items || []).filter((h) => h[selectedDef.ssh]) : health.items.filter((h) => h[field]))
      .map((h) => ({ ...h, meta: itemById.get(String(h.id)) }))
      .filter((h) => h.meta);
  }, [selectedDef, health, itemById]);

  // Comprobaciones activas cuya lista pública aún no se ha descargado ni una vez.
  const feedsPending = useMemo(() => Object.entries(FEED_OF_CHECK)
    .some(([check, feed]) => checks[check] && health?.feedsUpdatedAt && !health.feedsUpdatedAt[feed]), [checks, health]);
  const attributions = ATTRIBUTIONS.filter((a) => checks[a.check]);

  const detailLink = (h) => {
    if (selected === 'twoFactorAvailable') return h.twoFactorDocUrl;
    if (selected === 'passkeysAvailable') return h.passkeysDocUrl;
    return null;
  };

  return (
    <div className="sgc-health">
      <Card className="sgc-health__hero">
        <div>
          <p className="sgc-health__hero-label">{t('healthPage.score')}</p>
          {loadingHealth ? (
            <p className="sgc-health__loading">{t('common.loading')}</p>
          ) : (
            <HealthBar score={health?.summary?.healthScore ?? 0} />
          )}
        </div>
        <p className="sgc-health__hero-total">{t('healthPage.totalItems', { count: health?.summary?.total ?? 0 })}</p>
      </Card>

      <FeatureBanner
        icon={GlobeIcon}
        tone="accent"
        title={t('healthPage.darkWebTitle')}
        subtitle={checks.pwnedPasswords && health?.summary?.pwned !== undefined && !loadingHealth
          ? t('healthPage.pwnedCount', { count: health.summary.pwned })
          : t('healthPage.darkWebSubtitle')}
        onClick={checkingBreach ? undefined : () => loadHealth(true)}
      />
      {checkingBreach && <p className="sgc-health__checking">{t('healthPage.checkingBreach')}</p>}
      {feedsPending && <p className="sgc-health__checking" role="status">{t('healthPage.feedsPending')}</p>}

      <div className="sgc-health__rows">
        {rows.map((row) => (
          <button
            key={row.key}
            type="button"
            className={`sgc-health__row ${selected === row.key ? 'sgc-health__row--active' : ''}`}
            onClick={() => setSelected(selected === row.key ? null : row.key)}
          >
            <span className={`sgc-health__row-icon sgc-health__row-icon--${row.tone}`}>
              <row.icon width={18} height={18} aria-hidden="true" />
            </span>
            <span className="sgc-health__row-label">{t(`healthPage.rows.${row.key}`)}</span>
            <span className="sgc-health__row-count">{row.count}</span>
          </button>
        ))}
      </div>

      {selected && (
        <Card padded={false} className="sgc-health__detail">
          <div className="sgc-health__detail-header">
            <div>
              <h2>{t(`healthPage.rows.${selected}`)}</h2>
              {selectedDef?.ssh && <p className="sgc-health__detail-user">{t('healthPage.informational')}</p>}
            </div>
            <PillButton variant="secondary" size="sm" onClick={() => setSelected(null)}>{t('common.close')}</PillButton>
          </div>
          {selectedItems.length === 0 ? (
            <EmptyState icon={ActivityIcon} title={t('healthPage.emptyDetail')} />
          ) : (
            <ul className="sgc-health__detail-list">
              {selectedItems.map((h) => (
                <li key={h.id} className="sgc-health__detail-row">
                  <div className="sgc-health__detail-main">
                    <p className="sgc-health__detail-name">{h.meta.title}</p>
                    <p className="sgc-health__detail-user">{selectedDef?.ssh ? (h.keyType || '—') : (h.meta.username || '—')}</p>
                    {selected === 'breachedSites' && h.breachName && (
                      <p className="sgc-health__detail-note">{t('healthPage.breachDetail', { name: h.breachName, date: h.breachDate })}</p>
                    )}
                    {detailLink(h) && (
                      <a className="sgc-health__detail-doc" href={detailLink(h)} target="_blank" rel="noopener noreferrer">
                        {t('healthPage.docLink')}
                        <ExternalLinkIcon width={14} height={14} aria-hidden="true" />
                      </a>
                    )}
                  </div>
                  {!selectedDef?.ssh && <span className="sgc-health__detail-score">{h.score}</span>}
                  <PillButton variant="secondary" size="sm" onClick={() => navigate(`/vault/${h.id}`)}>
                    {t('healthPage.open')}
                  </PillButton>
                </li>
              ))}
            </ul>
          )}
        </Card>
      )}

      {health?.devScan && (
        <Card className="sgc-health__devscan" data-testid="dev-scan-card">
          <h2 className="sgc-health__devscan-title">{t('healthPage.devScan.title')}</h2>
          <ul className="sgc-health__devscan-list">
            <li><span>{t('healthPage.devScan.sshNotInVault')}</span><strong>{health.devScan.sshNotInVault}</strong></li>
            <li><span>{t('healthPage.devScan.sshUnprotected')}</span><strong>{health.devScan.sshUnprotected}</strong></li>
            <li><span>{t('healthPage.devScan.envSecrets')}</span><strong>{health.devScan.envSecrets}</strong></li>
          </ul>
          <p className="sgc-health__devscan-note">
            {t('healthPage.devScan.reported', { date: health.devScan.reportedAt ? new Date(health.devScan.reportedAt).toLocaleString() : '' })}
          </p>
        </Card>
      )}

      {attributions.length > 0 && (
        <p className="sgc-health__sources">
          <span>{t('healthPage.attributionTitle')}: </span>
          {attributions.map((a, i) => (
            <React.Fragment key={a.check}>
              {i > 0 && ' · '}
              <a href={a.href} target="_blank" rel="noopener noreferrer">{a.text}</a>
            </React.Fragment>
          ))}
        </p>
      )}
    </div>
  );
}

export default Health;
